import React from "react"

import { SimpleAudioPlayer } from "@/audio"
import { useTraceAudioContextTransport } from "@/traces"

/** Signed-URL-free location lookup; an hour matches the signed URL's own cache. */
const AUDIO_CONTEXT_CACHE_MS = 60 * 60 * 1000

/**
 * Recording for one uploaded conversation row, as an inline waveform player.
 *
 * The monitoring keyset row carries no audio location, so the S3 bucket/key are
 * resolved per row through the simulation audio-context read. Only the rows the
 * virtualized table has mounted issue that request, and the long stale window
 * keeps scrolling back over a row from repeating it.
 */
export const ConversationAudioCell = React.memo(
  ({ simulationOutputId }: { simulationOutputId: string | null }) => {
    const { data: audioContext } = useTraceAudioContextTransport(
      simulationOutputId ?? undefined,
      {
        gcTime: AUDIO_CONTEXT_CACHE_MS,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        staleTime: AUDIO_CONTEXT_CACHE_MS,
      },
    )

    if (!audioContext?.simulationS3Bucket || !audioContext.simulationS3Key) {
      return <span className="text-coval-text-muted">-</span>
    }

    return (
      <div
        role="presentation"
        className="flex w-full min-w-0 items-center"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="min-w-0 flex-1">
          <SimpleAudioPlayer
            bucket={audioContext.simulationS3Bucket}
            audioKey={audioContext.simulationS3Key}
            compact
          />
        </div>
      </div>
    )
  },
)

ConversationAudioCell.displayName = "ConversationAudioCell"
